import './QuizEditor.scss';

import * as React from 'react';
import { Dispatch, SetStateAction, useState } from 'react';

import {
  QUIZ_LOCK_INVALID_ERROR,
  QuestionTypes,
} from '@showbie-socrative/socrative-utils';
import { atom, useAtom, useSetAtom } from 'jotai';
import { useDispatch, useSelector } from 'react-redux';

import { translate } from '~l10n/client';
import { IconButton, SmallButton, UIButton } from '~shared/components/buttons';
import { PillButton } from '~shared/components/buttons/PillButton';
import { DialogConfirmation } from '~shared/components/Dialog';
import { IconArrow, IconTrash, IconX } from '~shared/components/icons/standard';
import { Spinner } from '~shared/components/Spinner/Spinner';
import { toast } from '~shared/components/Toast';
import { DropZoneProvider } from '~shared/providers/DropZoneProvider';
import { InvalidQuizModal } from '~teacher/components/InvalidQuizModal/InvalidQuizModal';
import {
  CurrentQuizEditorDialog,
  currentQuizEditorDialogAtom,
} from '~teacher/components/QuizEditor/atoms/currentQuizEditorDialogAtom';
import {
  questionIdsToDelete,
  selectedQuestionIds,
} from '~teacher/components/QuizEditor/atoms/selectedQuestionAtom';
import { useDeleteQuizUrl } from '~teacher/components/QuizEditor/contexts/DeleteQuizUrlContextProvider';
import { useQuizQuestionUrl } from '~teacher/components/QuizEditor/contexts/QuizQuestionUrlProvider';
import { useQuizUrl } from '~teacher/components/QuizEditor/contexts/QuizUrlContextProvider';
import { useProvideBulkQuestionDelete } from '~teacher/components/QuizEditor/contexts/UseBulkDeleteQuestionProvider';
import {
  ExtractQuestionsDialog,
  PaywallDialogExtractQuestionsFromDoc,
  extractQuestionsPositionAtom,
} from '~teacher/components/QuizEditor/ExtractQuestionsDialog';
import { GenerateQuestionDialog } from '~teacher/components/QuizEditor/GenerateQuestionDialog';
import { positionAtom as generateQuestionPositionAtom } from '~teacher/components/QuizEditor/GenerateQuestionDialog/atom';
import {
  ImportFromTextDialog,
  positionAtom as importFromTextPositionAtom,
} from '~teacher/components/QuizEditor/ImportFromTextDialog';
import { QuizEditorDeletedMessage } from '~teacher/components/QuizEditor/QuizEditorDeletedMessage';
import { ValidationError } from '~teacher/models/errors';
import { Invalidation } from '~teacher/models/quizzes';

import { isAxiosError } from '../../../api/isAxiosError';
import { ApplicationState } from '../../../rootReducer';
import { EditQuizStatus } from '../../slices/quiz/EditQuizStatus';
import {
  deleteQuiz,
  handleUnexpectedError,
  saveAndCreateNewQuestion,
  saveQuiz,
} from '../../slices/quiz/quizAppThunks';
import {
  makeSelectIsControlsDisabled,
  makeSelectIsQuestionDirty,
  makeSelectIsQuizDirty,
  selectEditingQuiz,
  selectQuizError,
  selectQuizStatus,
} from '../../slices/quiz/quizSelectors';
import {
  changeStatusToOk,
  changeStatusToSaving,
  finishEditingQuiz,
  removeQuestion,
} from '../../slices/quiz/quizSlice';
import {
  selectIsPaywallOpen,
  selectPaywallTouch,
} from '../../slices/teacher/teacherSelector';
import { closePaywall } from '../../slices/teacher/teacherSlice';
import { DeleteEmptyQuizDialog } from './DeleteEmptyQuizDialog';
import QuizEditorAddQuestion from './QuizEditorAddQuestion';
import {
  QuizEditorIsUploadingImageContext,
  QuizEditorIsUploadingImageProvider,
} from './QuizEditorIsUploadingImage';
import { QuizEditorPaywall } from './QuizEditorPaywall';
import QuizEditorQuizQuestions from './QuizEditorQuizQuestions';
import { ServerErrorDialog } from './ServerErrorDialog/ServerErrorDialog';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';

interface Props {
  isLocked?: boolean;
  onQuizDeleted: () => void;
  onSaveAndExit: () => void;
  router: any;
  refreshQuestions: () => Promise<void>;
  quizHeaderProp: (options: {
    isControlsDisabled: boolean;
    onHandleSaveAndExit: () => Promise<void>;
  }) => React.ReactNode;
}

function useOpenImportByTextDialog() {
  const setPosition = useSetAtom(importFromTextPositionAtom);
  const setCurrentQuizEditorDialog = useSetAtom(currentQuizEditorDialogAtom);

  return (position: number = null) => {
    setCurrentQuizEditorDialog(CurrentQuizEditorDialog.ImportFromText);
    setPosition(position);
  };
}

function useOpenExtractQuestionsDialog() {
  const setPosition = useSetAtom(extractQuestionsPositionAtom);
  const setCurrentQuizEditorDialog = useSetAtom(currentQuizEditorDialogAtom);

  return (position: number = null) => {
    setCurrentQuizEditorDialog(CurrentQuizEditorDialog.ExtractQuestionsFromDoc);
    setPosition(position);
  };
}

function useGenerateQuestionDialog() {
  const setPosition = useSetAtom(generateQuestionPositionAtom);
  const setCurrentQuizEditorDialog = useSetAtom(currentQuizEditorDialogAtom);

  const open = (position: number = null) => {
    setCurrentQuizEditorDialog(CurrentQuizEditorDialog.GenerateQuestions);
    setPosition(position);
  };

  const close = () => {
    setCurrentQuizEditorDialog(CurrentQuizEditorDialog.None);
    setPosition(null);
  };

  return {
    open,
    close,
  };
}

function useHandleSaveAndExit({
  showDeleteQuizModal,
  forceNavigate,
  onSaveAndExit,
  setInvalidations,
}: {
  showDeleteQuizModal: () => void;
  forceNavigate: (navigationCallback: () => void) => void;
  onSaveAndExit: () => void;
  setInvalidations: (
    value: ((prevState: Invalidation[]) => Invalidation[]) | Invalidation[]
  ) => void;
}) {
  const dispatch = useDispatch();
  const quizHasNoQuestions = useSelector(
    (state: ApplicationState): boolean =>
      (state.teachers?.quizData?.editQuiz?.questions?.length ?? 0) < 1
  );

  const quizUrl = useQuizUrl();
  const quizQuestionUrl = useQuizQuestionUrl();

  const selectQuestion = useSetAtom(selectedQuestionIds.addAtom);
  const unselectQuestion = useSetAtom(selectedQuestionIds.removeAtom);
  const [selectedQuestions] = useAtom(selectedQuestionIds.valueAtom);

  return async () => {
    if (quizHasNoQuestions) {
      showDeleteQuizModal();
      return;
    }

    try {
      await dispatch(
        saveQuiz({
          quizQuestionUrl: quizQuestionUrl,
          quizUrl: quizUrl,
          selectQuestion: selectQuestion,
          selectedQuestions: selectedQuestions,
          unselectQuestion: unselectQuestion,
        })
      );
      forceNavigate(onSaveAndExit);
    } catch (error) {
      if (error instanceof ValidationError) {
        setInvalidations(error.invalidations);
      } else {
        handleUnexpectedError(error);
      }
    }
  };
}

function useInsertQuestion(handleQuizError: (error: Invalidation[]) => void) {
  const dispatch = useDispatch();
  const quizQuestionUrl = useQuizQuestionUrl();

  const selectQuestion = useSetAtom(selectedQuestionIds.addAtom);
  const unselectQuestion = useSetAtom(selectedQuestionIds.removeAtom);
  const [selectedQuestions] = useAtom(selectedQuestionIds.valueAtom);

  return async (questionType: QuestionTypes, position: number) => {
    try {
      await dispatch(
        saveAndCreateNewQuestion({
          position,
          questionType,
          quizQuestionUrl,
          unselectQuestion: unselectQuestion,
          selectQuestion: selectQuestion,
          selectedQuestions: selectedQuestions,
        })
      );
    } catch (error) {
      if (error instanceof ValidationError) {
        handleQuizError(error.invalidations);
      } else {
        dispatch(handleUnexpectedError(error));
      }
    }
  };
}

function useFinishEditingEffect() {
  const dispatch = useDispatch();
  React.useEffect(() => {
    return () => {
      dispatch(finishEditingQuiz());
    };
  }, [dispatch]);
}

export default function QuizEditor({
  isLocked,
  onSaveAndExit,
  router,
  onQuizDeleted,
  quizHeaderProp,
  refreshQuestions,
}: Props) {
  const isForceNavigate = React.useRef(false);

  const [shouldShowBackToTop, setShouldShowBackToTop] = React.useState(false);
  const [storedRoute, setStoredRoute] = React.useState('');

  const [invalidations, setInvalidations] = React.useState<Invalidation[]>([]);

  const [currentQuizEditorDialog, setOpenDialog] = useAtom(
    currentQuizEditorDialogAtom
  );

  const quiz = useSelector(selectEditingQuiz);
  const isControlsDisabled = useSelector(makeSelectIsControlsDisabled());
  const error = useSelector(selectQuizError);
  const quizStatus = useSelector(selectQuizStatus);
  const isPaywallOpen = useSelector(selectIsPaywallOpen);
  const paywallTouch = useSelector(selectPaywallTouch);
  const isQuizDirty = useSelector(makeSelectIsQuizDirty());
  const isQuestionDirty = useSelector(makeSelectIsQuestionDirty());

  const renderInvalidQuizModal = () => {
    const closeModal = () => {
      setInvalidations([]);
    };

    // For now just display the first invalidation
    const invalidation = invalidations?.length > 0 ? invalidations[0] : null;
    return (
      <InvalidQuizModal onClose={closeModal} invalidation={invalidation} />
    );
  };

  const showUnsavedChangesModal = () => {
    setOpenDialog(CurrentQuizEditorDialog.UnsavedChanges);
  };

  const storeFutureRoute = () => {
    // Store the URL that the user attempted to navigate to incase we want to navigate to it later.
    const futureRoute = router.current();
    setStoredRoute(futureRoute);

    // replace the URL with the url of the current page.
    router.navigate(router.lastRoute, { replace: true });
  };
  const handleOnBeforeExecute = () => {
    if (isForceNavigate.current) {
      // Escape hatch to force navigation no matter what
      return false;
    } else if (quiz?.questions?.length === 0 && quiz?.lastUpdated !== null) {
      // If we delete all the questions, show the delete quiz modal
      // (but only if it isn't a new quiz)
      showDeleteQuizModal();
      storeFutureRoute();
      return true;
    } else if (isQuestionDirty || isQuizDirty) {
      storeFutureRoute();
      // Show the unsaved changes dialog if there are unsaved changes.
      showUnsavedChangesModal();
      return true;
    }
    return false;
  };

  const handleBeforeUnload = React.useCallback(
    (event: BeforeUnloadEvent) => {
      // The quiz lock invalid error shows a refresh button, we don't want to
      // show a messaeg when the refresh button is used.
      if (isAxiosError(error)) {
        const errorCode = error.response?.data?.code;
        if (errorCode === QUIZ_LOCK_INVALID_ERROR.code) return;
      }

      if (!isQuizDirty && !isQuestionDirty && isLocked === true) {
        return;
      }

      const message = translate(
        'Are you sure you want to leave without saving this quiz?'
      );
      // Chrome needs this otherwise this doesn't work.
      event.returnValue = message;
      return message;
    },
    [isQuizDirty, isQuestionDirty, error, isLocked]
  );

  const isMounted = React.useRef(false);
  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useFinishEditingEffect();

  const intersectionObserver = React.useRef<IntersectionObserver>(null);
  React.useEffect(() => {
    try {
      intersectionObserver.current = new IntersectionObserver(
        (entries) =>
          entries.forEach((item) => {
            const nextValue = item.isIntersecting;
            setShouldShowBackToTop(!nextValue);
          }),
        {
          root: undefined,
          rootMargin: '0px',
          threshold: 0,
        }
      );
    } catch (error) {
      console.error('IntersectionObserver not supported', error);
      intersectionObserver.current = {
        observe: () => {},
        disconnect: () => {},
      } as unknown as IntersectionObserver;
    }
    return () => {
      intersectionObserver.current?.disconnect();
    };
  }, []);

  React.useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  React.useEffect(() => {
    router.onBeforeExecute = handleOnBeforeExecute;
    return () => {
      router.onBeforeExecute = null;
    };
  });

  const topRef = React.useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const insertQuestion = useInsertQuestion(setInvalidations);

  const openImportByTextDialog = useOpenImportByTextDialog();
  const openExtractQuestionsDialog = useOpenExtractQuestionsDialog();
  const generateQuestion = useGenerateQuestionDialog();

  const handleBackToTopClicked = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleHideQuizEditorUnsavedChangesModal = () => {
    setOpenDialog(CurrentQuizEditorDialog.None);
  };

  const forceNavigate = (navigationCallback: () => void) => {
    if (isMounted.current) {
      isForceNavigate.current = true;
    }
    navigationCallback();
  };

  const navigateToStoredRoute = () => {
    forceNavigate(() => {
      router.navigate(storedRoute, { trigger: true });
    });
  };

  const closeErrorModal = () => {
    dispatch(changeStatusToOk());
  };

  const showDeleteQuizModal = () => {
    setOpenDialog(CurrentQuizEditorDialog.DeleteEmptyQuiz);
  };

  const handleSaveAndExit = useHandleSaveAndExit({
    onSaveAndExit,
    setInvalidations,
    forceNavigate,
    showDeleteQuizModal,
  });

  const handleHideDeleteEmptyQuizDialog = () => {
    setOpenDialog(CurrentQuizEditorDialog.None);
  };

  const deleteQuizUrl = useDeleteQuizUrl();
  const handleDeleteQuiz = async () => {
    try {
      await dispatch(
        deleteQuiz({ quizId: quiz.id, deleteQuizUrl: deleteQuizUrl })
      );
      if (storedRoute) {
        navigateToStoredRoute();
      } else {
        forceNavigate(onQuizDeleted);
      }
    } catch (e) {
      dispatch(handleUnexpectedError(e));
    }
  };

  if (!quiz) {
    return (
      <div className="my-12 mx-auto">
        <Spinner />
      </div>
    );
  }

  const { questions, id: quizId, deleted } = quiz;
  return (
    <QuizEditorIsUploadingImageProvider>
      {error !== null ? (
        <ServerErrorDialog
          open={quizStatus === EditQuizStatus.Error}
          onClose={closeErrorModal}
          error={error}
        />
      ) : null}

      <DropZoneProvider>
        <div
          className="px-8 sm:px-10 font-sans antialiased quiz-editor"
          data-quiz-editor
        >
          {deleted == true ? <QuizEditorDeletedMessage /> : null}

          {quizHeaderProp({
            isControlsDisabled: isLocked || isControlsDisabled,
            onHandleSaveAndExit: handleSaveAndExit,
          })}

          <div id="back-to-top-marker" ref={topRef} />

          <QuizEditorQuizQuestions
            isLocked={isLocked}
            questionIds={questions}
            quizId={quizId}
            insertQuestion={insertQuestion}
            openGenerateQuestionDialog={generateQuestion.open}
            openExtractQuestionsDialog={openExtractQuestionsDialog}
            openImportByTextDialog={openImportByTextDialog}
          />
          {!isLocked ? <FloatingQuizEditorActions /> : null}
          {!isLocked ? (
            <QuizEditorIsUploadingImageContext.Consumer>
              {({ isUploadingImages }) => (
                <QuizEditorAddQuestion
                  isDisabled={isControlsDisabled || isUploadingImages}
                  insertQuestion={insertQuestion}
                  quizQuestionLength={questions ? questions.length : 0}
                  openGenerateQuestionDialog={generateQuestion.open}
                  openExtractQuestionsDialog={openExtractQuestionsDialog}
                  openImportByTextDialog={openImportByTextDialog}
                />
              )}
            </QuizEditorIsUploadingImageContext.Consumer>
          ) : null}
        </div>
        {shouldShowBackToTop && (
          <div className="sticky bottom-0 right-0 mr-20 z-10 p-7 pr-10 flex justify-end pointer-events-none">
            <PillButton
              className="pointer-events-auto"
              iconOnly
              onClick={handleBackToTopClicked}
              aria-label={translate('Back to Top')}
            >
              <IconArrow />
            </PillButton>
          </div>
        )}

        {invalidations?.length > 0 && renderInvalidQuizModal()}

        <QuizEditorPaywall
          open={isPaywallOpen}
          onClose={() => dispatch(closePaywall())}
          paywallTouch={paywallTouch}
        />
      </DropZoneProvider>

      <UnsavedChangesDialog
        open={
          currentQuizEditorDialog === CurrentQuizEditorDialog.UnsavedChanges
        }
        onClose={handleHideQuizEditorUnsavedChangesModal}
        onOkay={navigateToStoredRoute}
      />

      <DeleteEmptyQuizDialog
        open={
          currentQuizEditorDialog === CurrentQuizEditorDialog.DeleteEmptyQuiz
        }
        onClose={handleHideDeleteEmptyQuizDialog}
        onOkay={handleDeleteQuiz}
      />
      <GenerateQuestionDialog
        refreshQuestions={refreshQuestions}
        onClose={() => setOpenDialog(CurrentQuizEditorDialog.None)}
      />
      <ImportFromTextDialog
        onClose={() => setOpenDialog(CurrentQuizEditorDialog.None)}
        onSuccess={() => {
          refreshQuestions().then(() =>
            setOpenDialog(CurrentQuizEditorDialog.None)
          );
        }}
      />
      <ExtractQuestionsDialog
        onClose={() => setOpenDialog(CurrentQuizEditorDialog.None)}
      />
      <PaywallDialogExtractQuestionsFromDoc />
      <DeleteConfirmationDialog quizId={quizId as any} />
    </QuizEditorIsUploadingImageProvider>
  );
}

function FloatingQuizEditorActions() {
  const [selectedItems] = useAtom(selectedQuestionIds.valueAtom);

  if (selectedItems.size === 0) {
    return null;
  }

  return <FloatingQuizEditorActionsContent />;
}

function FloatingQuizEditorActionsContent() {
  const [selectedItems] = useAtom(selectedQuestionIds.valueAtom);

  const [, reset] = useAtom(selectedQuestionIds.resetAtom);

  const [, setToDelete] = useAtom(questionIdsToDelete.replaceAtom);
  const [, deleteType] = useAtom(deleteTypeAtom);

  return (
    <>
      <div
        className="sticky z-101 bg-white rounded-6 shadow-modal py-2 px-5 h-16 inline-block"
        style={{ bottom: '20px' }}
      >
        <div className="flex gap-4">
          <SmallButton
            appearance="ghost"
            onClick={() => {
              setToDelete(selectedItems);
              deleteType('bulk');
            }}
          >
            <IconTrash />
            {translate('Delete')}
          </SmallButton>
          <div className="flex flex-col justify-center">
            <span className="text-blue-900  text-sm">
              {translate('{0} selected').format(selectedItems.size)}
            </span>
          </div>
          <IconButton
            onClick={() => {
              reset();
            }}
          >
            <IconX
              title={translate('deselect all items')}
              titleId="deselect-all-questions"
            />
          </IconButton>
        </div>
      </div>
    </>
  );
}

export interface BulkDeleteConfirmationDialogProps {
  quizId: number;
}

function useHandleDelete({
  setIsDisabled,
  quizId,
  onClose,
}: {
  setIsDisabled: Dispatch<SetStateAction<boolean>>;
  quizId: number;
  onClose: (openState: false) => void;
}) {
  const dispatch = useDispatch();
  const [selectedItems] = useAtom(questionIdsToDelete.valueAtom);
  const [, reset] = useAtom(questionIdsToDelete.resetAtom);
  const [, removeSelectedQuestionId] = useAtom(selectedQuestionIds.removeAtom);

  const bulkDeleteQuestionMutation = useProvideBulkQuestionDelete();
  return () => {
    setIsDisabled(true);
    dispatch(changeStatusToSaving());
    bulkDeleteQuestionMutation.mutate(
      {
        quizId,
        questionIds: [...selectedItems.values()] as any[],
      },
      {
        onSuccess: (_, { questionIds }) => {
          reset();
          onClose(false);

          /* Unselect all the deleted questions
           * If you used the action bar it's all of them
           * If you used the trash icon next to a single question it's just one
           */
          questionIds
            .map((questionId) => `${questionId}`)
            .forEach(removeSelectedQuestionId);

          dispatch(
            removeQuestion({
              questionIds: questionIds.map((questionId) => `${questionId}`),
            })
          );
        },
        onError: (error) => {
          if (__DEV__) {
            console.error(error);
          }
          setIsDisabled(false);
          toast.critical(translate('An error occurred while trying to delete'));
        },
        onSettled: () => {
          dispatch(changeStatusToOk());
        },
      }
    );
  };
}

// Track where the delete dialog was triggered from
export const deleteTypeAtom = atom<'single' | 'bulk'>('single');

function DeleteConfirmationDialog({
  quizId,
}: BulkDeleteConfirmationDialogProps) {
  const [toDelete] = useAtom(questionIdsToDelete.valueAtom);
  const [, resetToDelete] = useAtom(questionIdsToDelete.resetAtom);

  const [itemsToDelete] = useAtom(questionIdsToDelete.valueAtom);
  const [isDisabled, setIsDisabled] = useState(false);

  const onClose = () => {
    setIsDisabled(false);
    resetToDelete();
  };

  const handleDelete = useHandleDelete({
    setIsDisabled: setIsDisabled,
    quizId: quizId,
    onClose: onClose,
  });

  return (
    <DialogConfirmation
      id="confirm-delete"
      open={toDelete.size > 0}
      title={
        itemsToDelete.size === 1
          ? translate('Delete Forever?')
          : translate('Delete {0} Items Forever?').format(itemsToDelete.size)
      }
      onClose={onClose}
      closeLabel={translate('Close')}
      actions={[
        <UIButton key="Cancel" isDisabled={isDisabled} onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <UIButton
          key="Delete"
          appearance="risky"
          isDisabled={isDisabled}
          onClick={handleDelete}
        >
          {translate('Delete')}
        </UIButton>,
      ]}
    >
      {itemsToDelete.size === 1
        ? translate(
            'Are you sure you want to permanently delete the selected item and its contents?'
          )
        : translate(
            'Are you sure you want to permanently delete the selected items and their contents?'
          )}
    </DialogConfirmation>
  );
}
